/**
 * Where a spare could stand in, said the way she'd say it: by client, naming spots only when
 * the spare fits some of that client's spots on the route but not all.
 */
import type { Catalog, SpareReport } from "@hitl/rotations-domain";

export interface SpareFitsLine {
  readonly text: string;
  /** True when the spare fits nowhere on the route: worth swapping for another pot. */
  readonly none: boolean;
}

export function spareFitsLine(catalog: Catalog, spare: SpareReport, routeSlotIds: readonly string[]): SpareFitsLine {
  const route = [...new Set(routeSlotIds)];
  const fitting = new Set(spare.fits.map((f) => f.slotId));
  if (fitting.size === 0) return { text: "Fits no spot on this route", none: true };
  if (route.every((slotId) => fitting.has(slotId))) return { text: "Fits every stop", none: false };

  const clients = [...new Set(route.map((slotId) => catalog.slot(slotId).clientId))];
  const parts = clients.flatMap((clientId) => {
    const spots = route.filter((slotId) => catalog.slot(slotId).clientId === clientId);
    const fits = spots.filter((slotId) => fitting.has(slotId));
    if (fits.length === 0) return [];
    const name = catalog.client(clientId)?.name ?? clientId;
    if (fits.length === spots.length) return [name];
    return [`${name} (${fits.map((slotId) => catalog.slot(slotId).label).join(", ")} only)`];
  });
  return { text: `Fits ${parts.join(", ")}`, none: false };
}
